import { useEffect, useMemo, useState } from 'react'

import type { AIModel } from '@/api/types'
import { useAIModels } from '@/features/studio/hooks/useAIModels'

const STORAGE_KEY = 'silkreels.script.text_model_id'

export function useScriptTextModel() {
  const { byType, defaultModelId, loading, error } = useAIModels()
  const textModels = useMemo(() => byType('text'), [byType])
  const [modelId, setModelIdState] = useState(() => {
    if (typeof window === 'undefined') return ''
    return window.localStorage.getItem(STORAGE_KEY) ?? ''
  })

  useEffect(() => {
    if (loading || textModels.length === 0) return
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
    if (stored && textModels.some((m) => m.id === stored)) {
      if (modelId !== stored) setModelIdState(stored)
      return
    }
    const fallback = defaultModelId('text')
    if (fallback && modelId !== fallback) {
      setModelIdState(fallback)
    }
  }, [loading, textModels, defaultModelId, modelId])

  function setModelId(id: string) {
    setModelIdState(id)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, id)
    }
  }

  const selectedModel: AIModel | undefined = textModels.find((m) => m.id === modelId)

  return {
    modelId,
    setModelId,
    textModels,
    selectedModel,
    loading,
    error,
    ready: !loading && textModels.length > 0 && !!modelId,
  }
}
